import type React from 'react';
import { useIntl } from 'react-intl';

import { FlexColumn } from 'ui-components/components/flex/column';
import { FloatTextArea } from 'ui-components/components/float-text-area';
import { Gap } from 'ui-components/components/flex/gap';
import { INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message';
import { Input, version2 } from 'ui-components/components/input';
import { Text } from 'ui-components/components/text';
import { TextType } from 'ui-components/components/text/text-type';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { AutomationWorkflows } from '../../../../../constants/features';
import { LabsFeedbackButton } from '../../../labs/components/labs-feedback-button/labs-feedback-button';
import { LABS_FEATURES } from '../../../labs/labs-feature-constants';
import type { LabsFeature } from '../../../labs/typedefs';
import { messages } from './messages';
import styles from './styles.module.less';

const automationWorkflowsFeature = LABS_FEATURES.find((f: LabsFeature) => f.id === AutomationWorkflows);

type WorkflowDetailsPanelProps = {
  name: string;
  description: string;
  showNameError: boolean;
  onNameChange: (name: string) => void;
  onDescriptionChange: (description: string) => void;
};

export const WorkflowDetailsPanel: React.FC<WorkflowDetailsPanelProps> = ({
  name,
  description,
  showNameError,
  onNameChange,
  onDescriptionChange,
}) => {
  const intl = useIntl();

  return (
    <FlexColumn className={styles['details-panel']} gap={Gap.Space24}>
      <FlexColumn gap={Gap.Space8}>
        <Text textType={TextType.Label} value={messages.nameLabel} />
        <Input
          inlineMessage={showNameError ? intl.formatMessage(messages.nameRequired) : undefined}
          inlineMessageType={showNameError ? INLINE_MESSAGE_TYPES.error : undefined}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onNameChange(e.target.value)}
          placeholder={intl.formatMessage(messages.namePlaceholder)}
          value={name}
          version={version2}
        />
      </FlexColumn>

      <FlexColumn gap={Gap.Space8}>
        <Text textType={TextType.Label} value={messages.descriptionLabel} />
        <FloatTextArea
          className={styles.textarea}
          onChange={onDescriptionChange}
          placeholder={intl.formatMessage(messages.descriptionPlaceholder)}
          value={description}
        />
      </FlexColumn>
      {automationWorkflowsFeature && (
        <div className={styles['feedback-button']}>
          <LabsFeedbackButton feature={automationWorkflowsFeature} theme={THEME_CONSTANTS.secondary} />
        </div>
      )}
    </FlexColumn>
  );
};
